import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { logError } from '../lib/logger';

const ADSENSE_CLIENT_ID = 'ca-pub-2349694359753990';
const INTERSTITIAL_AD_SLOT = 'auto';
const ENABLE_LIVE_ADS = true;

const InterstitialAd = ({ targetUrl, onClose, autoRedirectDelay = 5 }) => {
  const [countdown, setCountdown] = useState(autoRedirectDelay);
  const [adLoaded, setAdLoaded] = useState(false);

  useEffect(() => {
    const loadTimer = setTimeout(() => {
      setAdLoaded(true);
      if (ENABLE_LIVE_ADS && window.adsbygoogle) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (err) {
          logError('InterstitialAd:adsense', err);
        }
      }
    }, 500);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(loadTimer);
      clearInterval(countdownInterval);
    };
  }, []);

  const handleRedirect = () => {
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const handleSkip = () => {
    handleRedirect();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
      data-testid="interstitial-ad"
    >
      <div className="relative bg-white rounded-xl sm:rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all"
          aria-label="Close ad"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Ad Content */}
        <div className="flex flex-col">
          {/* Ad Container */}
          <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-8">
            {!adLoaded ? (
              <div className="flex items-center justify-center h-64 sm:h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm">Loading...</p>
                </div>
              </div>
            ) : (
              <div className="h-64 sm:h-[400px] flex items-center justify-center">
                {ENABLE_LIVE_ADS ? (
                  <ins 
                    className="adsbygoogle"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    data-ad-client={ADSENSE_CLIENT_ID}
                    data-ad-slot={INTERSTITIAL_AD_SLOT}
                    data-ad-format="auto"
                    data-full-width-responsive="true"
                  ></ins>
                ) : (
                  <div className="text-center px-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-12 max-w-2xl">
                      <div className="text-4xl sm:text-6xl mb-4 sm:mb-6">📰</div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                        Advertisement
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                        Interstitial Ad Space
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Enable live ads by setting ENABLE_LIVE_ADS = true
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom Bar */}
          <div className="bg-white border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              <p className="font-medium">Redirecting in {countdown} seconds...</p>
              <p className="text-xs text-gray-500 mt-1">You'll be taken to the original source</p>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSkip}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                data-testid="skip-ad-button"
              >
                Continue →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterstitialAd;
